import { Link } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';

export function TermsPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <Mail className="w-8 h-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">FormTo.cc</span>
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
        <p className="text-gray-500 mb-8">Last updated: January 6, 2025</p>

        <div className="prose prose-gray max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-600 mb-4">
              By accessing or using FormTo.cc ("Service"), you agree to be bound by these
              Terms of Service ("Terms"). If you do not agree to these Terms, you may not
              use the Service.
            </p>
            <p className="text-gray-600">
              We may update these Terms from time to time. Continued use of the Service
              after changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
            <p className="text-gray-600 mb-4">
              FormTo.cc is a form-to-Telegram notification service that enables users to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Create webhook endpoints to receive form submissions</li>
              <li>Connect Telegram chats to receive instant notifications</li>
              <li>Configure message formatting and field mappings</li>
              <li>View submission history and analytics</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Account Registration</h2>
            <p className="text-gray-600 mb-4">
              To use certain features of the Service, you must create an account. You agree to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Accept responsibility for all activities under your account</li>
            </ul>
            <p className="text-gray-600 mt-4">
              You must be at least 16 years old to create an account and use the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Acceptable Use</h2>
            <p className="text-gray-600 mb-4">
              You agree not to use the Service to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Violate any applicable laws or regulations</li>
              <li>Transmit spam, malware, or malicious content</li>
              <li>Collect or harvest personal data without consent</li>
              <li>Impersonate others or misrepresent your identity</li>
              <li>Interfere with the operation of the Service</li>
              <li>Attempt to gain unauthorized access to systems</li>
              <li>Use automated means to abuse or overload the Service</li>
              <li>Transmit illegal, harmful, threatening, or abusive content</li>
              <li>Infringe on intellectual property rights of others</li>
            </ul>
            <p className="text-gray-600 mt-4">
              We reserve the right to suspend or terminate accounts that violate these terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Your Content</h2>
            <p className="text-gray-600 mb-4">
              You retain ownership of all content you submit through the Service. By using
              the Service, you grant us a limited license to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Store and process form submissions to deliver notifications</li>
              <li>Display submission data in your dashboard</li>
              <li>Generate analytics based on your usage</li>
            </ul>
            <p className="text-gray-600 mt-4">
              You are responsible for ensuring you have the right to collect and process
              any data submitted through your forms, including compliance with privacy laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Intellectual Property</h2>
            <p className="text-gray-600 mb-4">
              The Service, including its design, features, and content (excluding user content),
              is owned by FormTo.cc and protected by intellectual property laws.
            </p>
            <p className="text-gray-600">
              We grant you a limited, non-exclusive, non-transferable license to use the
              Service in accordance with these Terms. You may not copy, modify, distribute,
              or reverse engineer any part of the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Third-Party Services</h2>
            <p className="text-gray-600 mb-4">
              The Service integrates with third-party services, including:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>
                <strong>Telegram:</strong> For message delivery. Your use of Telegram is
                subject to Telegram's terms of service.
              </li>
              <li>
                <strong>AWS:</strong> For infrastructure and hosting. Data is processed
                according to AWS's security and privacy practices.
              </li>
            </ul>
            <p className="text-gray-600 mt-4">
              We are not responsible for the practices of third-party services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Service Availability</h2>
            <p className="text-gray-600 mb-4">
              We strive to maintain high availability but do not guarantee uninterrupted
              access to the Service. The Service may be unavailable due to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Scheduled maintenance (we will provide notice when possible)</li>
              <li>System failures or technical issues</li>
              <li>Third-party service outages (AWS, Telegram)</li>
              <li>Force majeure events</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Rate Limits and Fair Use</h2>
            <p className="text-gray-600 mb-4">
              To ensure service quality for all users, we implement rate limits on form
              submissions and API requests. These limits may vary by plan tier.
            </p>
            <p className="text-gray-600">
              Excessive usage that impacts other users or system stability may result in
              temporary or permanent restrictions on your account.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Payment Terms</h2>
            <p className="text-gray-600 mb-4">
              Certain features may require a paid subscription. For paid plans:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Fees are billed in advance on a monthly or annual basis</li>
              <li>All fees are non-refundable unless required by law</li>
              <li>We may change pricing with 30 days notice</li>
              <li>Failure to pay may result in service suspension</li>
            </ul>
            <p className="text-gray-600 mt-4">
              Free tier users may be subject to usage limits as specified on our pricing page.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Termination</h2>
            <p className="text-gray-600 mb-4">
              You may terminate your account at any time by contacting us. We may terminate
              or suspend your account immediately if you:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Violate these Terms</li>
              <li>Engage in fraudulent or illegal activity</li>
              <li>Fail to pay applicable fees</li>
              <li>Create risk or legal exposure for us</li>
            </ul>
            <p className="text-gray-600 mt-4">
              Upon termination, your right to use the Service ceases immediately.
              We may delete your data according to our data retention policies.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Disclaimer of Warranties</h2>
            <p className="text-gray-600 mb-4">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF
              ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Merchantability and fitness for a particular purpose</li>
              <li>Accuracy, reliability, or completeness of content</li>
              <li>Uninterrupted, secure, or error-free operation</li>
              <li>Results obtained from using the Service</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Limitation of Liability</h2>
            <p className="text-gray-600 mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, FORMTO.CC SHALL NOT BE LIABLE FOR:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Indirect, incidental, special, or consequential damages</li>
              <li>Loss of profits, data, or business opportunities</li>
              <li>Damages arising from service interruptions</li>
              <li>Damages exceeding the amount paid by you in the past 12 months</li>
            </ul>
            <p className="text-gray-600 mt-4">
              Some jurisdictions do not allow limitation of liability, so these
              limitations may not apply to you.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Indemnification</h2>
            <p className="text-gray-600">
              You agree to indemnify and hold harmless FormTo.cc, its officers, directors,
              employees, and agents from any claims, damages, losses, or expenses arising
              from your use of the Service, violation of these Terms, or infringement of
              any rights of third parties.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Governing Law</h2>
            <p className="text-gray-600">
              These Terms shall be governed by and construed in accordance with the laws
              of the jurisdiction in which FormTo.cc operates, without regard to conflict
              of law principles. Any disputes arising from these Terms shall be resolved
              in the courts of that jurisdiction.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">16. Severability</h2>
            <p className="text-gray-600">
              If any provision of these Terms is found to be unenforceable, the remaining
              provisions will continue in full force and effect. The unenforceable provision
              will be modified to reflect the parties' original intent.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">17. Entire Agreement</h2>
            <p className="text-gray-600">
              These Terms, together with our Privacy Policy, constitute the entire
              agreement between you and FormTo.cc regarding the use of the Service.
              Any prior agreements or understandings are superseded by these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">18. Contact Us</h2>
            <p className="text-gray-600">
              If you have questions about these Terms of Service, please contact us at:
            </p>
            <ul className="list-none mt-4 text-gray-600 space-y-2">
              <li>
                Email:{' '}
                <a href="mailto:legal@formto.cc" className="text-blue-600 hover:underline">
                  legal@formto.cc
                </a>
              </li>
              <li>
                Website:{' '}
                <a href="https://formto.cc" className="text-blue-600 hover:underline">
                  https://formto.cc
                </a>
              </li>
            </ul>
          </section>
        </div>
      </main>

      {/* Simple Footer */}
      <footer className="bg-gray-100 border-t border-gray-200 py-8">
        <div className="container mx-auto px-4 text-center text-gray-600">
          <p>&copy; 2025 FormTo.cc. All rights reserved.</p>
          <div className="mt-2 space-x-4">
            <Link to="/privacy" className="text-blue-600 hover:underline">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-blue-600 hover:underline">
              Terms of Service
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
